import { useEffect, useRef } from 'react';
import { useRewardsStore } from '@/store/rewards.store';
import { CreatureSvg } from './CreatureSvg';
import { RarityPill } from './RarityPill';
import { getSpecies, RARITY_CONFIG } from '@/constants/rewards';
import type { Rarity } from '@/types/rewards';

export function CreatureDetailModal() {
  const speciesId = useRewardsStore((s) => s.detailCreatureId);
  const setDetailCreatureId = useRewardsStore((s) => s.setDetailCreatureId);
  const collection = useRewardsStore((s) => s.collection);
  const dialogRef = useRef<HTMLDialogElement>(null);

  const species = speciesId ? getSpecies(speciesId) : null;
  const ownedEntry = speciesId
    ? collection?.ownedSpecies.find((o) => o.speciesId === speciesId)
    : null;

  useEffect(() => {
    if (speciesId) {
      dialogRef.current?.showModal();
    } else {
      dialogRef.current?.close();
    }
  }, [speciesId]);

  const handleClose = () => setDetailCreatureId(null);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  if (!species || !speciesId) return null;

  const rarityConfig = RARITY_CONFIG[species.rarity as Rarity];

  return (
    <dialog
      ref={dialogRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      style={{
        margin: 'auto',
        border: 'none',
        background: 'transparent',
        maxWidth: '400px',
        width: '100%',
      }}
      aria-labelledby="detail-creature-name"
    >
      <div className="rounded-3xl bg-white shadow-2xl overflow-hidden w-full animate-reveal-in">
        {/* Header strip */}
        <div
          className="flex items-center justify-center py-8 relative"
          style={{
            background: `radial-gradient(circle at 50% 60%, ${rarityConfig.glow}, ${rarityConfig.bg} 80%)`,
          }}
        >
          <CreatureSvg speciesId={speciesId} size={160} />
          <button
            onClick={handleClose}
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/80 hover:bg-white text-stone-500 hover:text-stone-800 flex items-center justify-center text-lg transition-colors"
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          <div className="flex items-center gap-2 mb-1">
            <RarityPill rarity={species.rarity as Rarity} />
          </div>
          <h2
            id="detail-creature-name"
            className="text-2xl font-display font-bold text-stone-900 mb-1"
          >
            {species.name}
          </h2>
          <p className="text-sm text-stone-500 mb-4">{species.tagline}</p>

          {/* Lore */}
          <div className="bg-stone-50 rounded-xl px-4 py-3 text-sm text-stone-600 leading-relaxed mb-5">
            {species.lore}
          </div>

          {/* Stats */}
          {ownedEntry && (
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center">
                <p className="text-xs text-stone-400 mb-0.5">First met</p>
                <p className="text-sm font-semibold text-stone-700">
                  {new Date(ownedEntry.firstHatchedAt).toLocaleDateString()}
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-stone-400 mb-0.5">Hatched</p>
                <p className="text-sm font-semibold text-stone-700">&times;{ownedEntry.count}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-stone-400 mb-0.5">First egg</p>
                <p className="text-sm font-semibold text-stone-700 capitalize">
                  {ownedEntry.firstEggTier}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </dialog>
  );
}
